/**
 * Chat-created artifacts for the header stack.
 *
 * Pulls generated images, Spaces items (notes/tasks/…), plans, and virtual
 * files from assistant messages so the chat header can house everything
 * Losi created in this thread — not only images.
 */

import { extractGeneratedChatImages } from '@/lib/chat/generated-images'
import {
  parseSidekickSpaceItemCardTags,
  parseSidekickSpaceItemTags,
  type SidekickSpaceItemContext,
  type SidekickSpaceItemType
} from '@/lib/sidekick/space-item-context'

export type ChatCreatedArtifactKind =
  | 'image'
  | 'plan'
  | 'virtual_file'
  | 'pdf'
  | SidekickSpaceItemType

export type ChatCreatedArtifact =
  | {
      id: string
      kind: 'image'
      title: string
      subtitle?: string
      url: string
      alt: string
      messageId: string
    }
  | {
      id: string
      kind: 'plan'
      title: string
      subtitle?: string
      planId: string
      content: string
      status?: string
      messageId: string
    }
  | {
      id: string
      kind: 'virtual_file'
      title: string
      subtitle?: string
      fileId: string
      path: string
      name: string
      messageId: string
    }
  | {
      id: string
      kind: 'pdf'
      title: string
      subtitle?: string
      url: string
      fileName: string
      messageId: string
    }
  | {
      id: string
      kind: SidekickSpaceItemType
      title: string
      subtitle?: string
      spaceItem: SidekickSpaceItemContext
      messageId: string
    }

export type ChatCreatedArtifactMessage = {
  id: string
  content?: unknown
  annotation?: unknown
  created_at?: string | null
  role?: string | null
  sequence_number?: number | null
}

const VIRTUAL_FILE_KEYS = [
  'VirtualFilesystem__losi__fs_write_file',
  'VirtualFilesystem__losi__fs_patch_file'
] as const

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

function annotationRecords(annotation: unknown): Record<string, unknown>[] {
  if (Array.isArray(annotation)) {
    return annotation.map(asRecord).filter(Boolean) as Record<string, unknown>[]
  }
  const single = asRecord(annotation)
  return single ? [single] : []
}

function parseVirtualFiles(entry: unknown): Array<{
  id: string
  path: string
  name: string
}> {
  const raw =
    typeof entry === 'string'
      ? (() => {
          try {
            return JSON.parse(entry) as unknown
          } catch {
            return null
          }
        })()
      : entry

  const objects: Record<string, unknown>[] = []
  if (Array.isArray(raw)) {
    for (const item of raw) {
      const rec = asRecord(item)
      if (rec) objects.push(rec)
    }
  } else {
    const rec = asRecord(raw)
    if (rec) objects.push(rec)
  }

  const files: Array<{ id: string; path: string; name: string }> = []
  for (const obj of objects) {
    if (typeof obj.id === 'string' && typeof obj.path === 'string') {
      files.push({
        id: obj.id,
        path: obj.path,
        name:
          (typeof obj.name === 'string' && obj.name) ||
          obj.path.split('/').pop() ||
          obj.path
      })
    }
    if (Array.isArray(obj.files)) {
      for (const file of obj.files) {
        const rec = asRecord(file)
        if (!rec || typeof rec.id !== 'string' || typeof rec.path !== 'string') {
          continue
        }
        files.push({
          id: rec.id,
          path: rec.path,
          name:
            (typeof rec.name === 'string' && rec.name) ||
            rec.path.split('/').pop() ||
            rec.path
        })
      }
    }
  }
  return files
}

function spaceItemLabel(type: SidekickSpaceItemType): string {
  switch (type) {
    case 'note':
      return 'Note'
    case 'task':
      return 'Task'
    case 'sheet':
      return 'Sheet'
    case 'design':
      return 'Design'
    case 'whiteboard':
      return 'Whiteboard'
    case 'sprint':
      return 'Sprint'
    case 'folder':
      return 'Folder'
    case 'conversation':
    case 'chat':
      return 'Chat'
    case 'event':
      return 'Event'
    case 'file':
      return 'File'
    case 'collection':
      return 'Collection'
    case 'space':
      return 'Space'
    default:
      return 'Item'
  }
}

/**
 * Newest-first list of durable things Losi created in this chat.
 * Dedupes by stable id (image url / planId / space item / virtual file path).
 */
export function extractChatCreatedArtifacts(
  messages: ChatCreatedArtifactMessage[]
): ChatCreatedArtifact[] {
  const artifacts: ChatCreatedArtifact[] = []
  const seen = new Set<string>()

  const push = (artifact: ChatCreatedArtifact) => {
    if (seen.has(artifact.id)) return
    seen.add(artifact.id)
    artifacts.push(artifact)
  }

  const images = extractGeneratedChatImages(messages)
  for (const image of images) {
    push({
      id: `image:${image.url}`,
      kind: 'image',
      title: image.name || image.alt || 'Generated image',
      subtitle: 'Image',
      url: image.url,
      alt: image.alt,
      messageId: image.messageId
    })
  }

  const ordered = [...messages].sort((left, right) => {
    const leftSeq = left.sequence_number ?? Number.NEGATIVE_INFINITY
    const rightSeq = right.sequence_number ?? Number.NEGATIVE_INFINITY
    if (leftSeq !== rightSeq) return rightSeq - leftSeq
    const leftTime = left.created_at ? Date.parse(left.created_at) : 0
    const rightTime = right.created_at ? Date.parse(right.created_at) : 0
    return rightTime - leftTime
  })

  for (const message of ordered) {
    if (message.role && message.role !== 'assistant') continue

    const content = typeof message.content === 'string' ? message.content : ''
    if (content) {
      const fromCards = parseSidekickSpaceItemCardTags(content).items
      const fromTags = parseSidekickSpaceItemTags(content).items
      for (const item of [...fromCards, ...fromTags]) {
        push({
          id: `space:${item.type}:${item.spaceId}:${item.id}`,
          kind: item.type,
          title: item.title || spaceItemLabel(item.type),
          subtitle: item.summary || spaceItemLabel(item.type),
          spaceItem: item,
          messageId: message.id
        })
      }
    }

    for (const annotation of annotationRecords(message.annotation)) {
      const showPlan = asRecord(annotation.show_plan)
      if (showPlan && typeof showPlan.planId === 'string') {
        const contentText =
          typeof showPlan.content === 'string' ? showPlan.content : ''
        push({
          id: `plan:${showPlan.planId}`,
          kind: 'plan',
          title: 'Execution plan',
          subtitle:
            typeof showPlan.status === 'string'
              ? showPlan.status.replace(/_/g, ' ')
              : 'Plan',
          planId: showPlan.planId,
          content: contentText,
          status:
            typeof showPlan.status === 'string' ? showPlan.status : undefined,
          messageId: message.id
        })
      }

      for (const key of VIRTUAL_FILE_KEYS) {
        const raw = annotation[key]
        const entry =
          raw && typeof raw === 'object' && 'result' in raw
            ? (raw as { result?: unknown }).result
            : raw
        for (const file of parseVirtualFiles(entry)) {
          push({
            id: `vfs:${file.path}`,
            kind: 'virtual_file',
            title: file.name,
            subtitle: file.path,
            fileId: file.id,
            path: file.path,
            name: file.name,
            messageId: message.id
          })
        }
      }

      for (const [key, raw] of Object.entries(annotation)) {
        if (
          key !== 'createPdfDocument' &&
          !key.endsWith('__createPdfDocument') &&
          !key.toLowerCase().includes('createpdfdocument')
        ) {
          continue
        }
        const entry =
          raw && typeof raw === 'object' && 'result' in raw
            ? (raw as { result?: unknown }).result
            : raw
        const rec = asRecord(entry)
        if (!rec) continue
        const url = typeof rec.url === 'string' ? rec.url : ''
        if (!url.startsWith('http')) continue
        const fileName =
          (typeof rec.fileName === 'string' && rec.fileName) ||
          (typeof rec.file_name === 'string' && rec.file_name) ||
          'document.pdf'
        push({
          id: `pdf:${url}`,
          kind: 'pdf',
          title: fileName,
          subtitle: 'PDF',
          url,
          fileName,
          messageId: message.id
        })
      }
    }
  }

  return artifacts
}

export function chatCreatedArtifactKindLabel(
  kind: ChatCreatedArtifactKind
): string {
  if (kind === 'image') return 'Image'
  if (kind === 'plan') return 'Plan'
  if (kind === 'virtual_file') return 'File'
  if (kind === 'pdf') return 'PDF'
  return spaceItemLabel(kind)
}
